import {
  getNetworkConfig,
} from "demo-wallet-shared/build/helpers/getNetworkConfig";
import {
  normalizeHomeDomainUrl,
} from "demo-wallet-shared/build/helpers/normalizeHomeDomainUrl";
import {
  checkTomlForFields,
} from "demo-wallet-shared/build/methods/checkTomlForFields";
import { TomlFields } from "demo-wallet-shared/build/types/types";
import {
  sep10AuthSend,
  sep10AuthSign,
  sep10AuthStart,
} from "demo-wallet-shared/build/methods/sep10Auth";
import {
  sep45AuthSend,
  sep45AuthSign,
  sep45AuthStart,
} from "demo-wallet-shared/build/methods/sep45Auth";
import { AnchorActionType } from "../types/types";
import { log } from "demo-wallet-shared/build/helpers/log";
import { custodialSelector } from "./custodial";
import { getToml } from "demo-wallet-shared/build/methods/getToml";
import { isEmpty } from "lodash";
import {
  checkDepositWithdrawInfo
} from "demo-wallet-shared/build/methods/checkDepositWithdrawInfo";

export interface Sep10AuthParams {
  publicKey: string;
  secretKey: string;
  homeDomain: string;
  clientDomain: string;
  walletBackendEndpoint: string;
  custodialMode?: {
    isEnabled: boolean;
    publicKey: string;
    secretKey: string;
    memoId: string;
  };
}

export interface Sep45AuthParams {
  contractId: string;
  homeDomain: string;
  clientDomain: string;
  authEndpoint?: string; // Optional override, defaults to sep45 server
}

/**
 * Reusable SEP-10 authentication utility
 * Returns JWT token for authenticated requests
 */
export const authenticateWithSep10 = async (
  anchorActionType: AnchorActionType,
  assetCode: string,
  assetIssuer: string,
  clientDomain: string,
  homeDomain: string,
  publicKey: string,
  requiredKeys: TomlFields[],
  secretKey: string,
  sepName: string,
  state: any,
  walletBackendEndpoint: string,
): Promise<string> => {
  log.instruction({
    title: `Initiating a ${sepName} for classic account`,
  });

  const networkConfig = getNetworkConfig();
  const {
    isEnabled: custodialIsEnabled,
    secretKey: custodialSecretKey,
    publicKey: custodialPublicKey,
    memoId: custodialMemoId,
  } = custodialSelector(state);
  // This is unlikely
  if (
    custodialIsEnabled &&
    !(custodialSecretKey && custodialPublicKey && custodialMemoId)
  ) {
    throw new Error(
      "Custodial mode requires secret key, public key, and memo ID",
    );
  }

  // Check TOML for required SEP-10 fields
  const tomlResponse = await checkTomlForFields({
    sepName,
    assetIssuer,
    requiredKeys,
    networkUrl: networkConfig.url,
    homeDomain,
  });

  await  checkDepositWithdrawInfo ({
    type: anchorActionType,
    transferServerUrl: sepName.startsWith("SEP-24") ? tomlResponse.TRANSFER_SERVER_SEP0024 : tomlResponse.TRANSFER_SERVER,
    assetCode,
  })

  log.instruction({
    title: `${sepName} is enabled, and requires authentication so we should go through SEP-10`,
  });

  // SEP-10 start
  const challengeTransaction = await sep10AuthStart({
    authEndpoint: tomlResponse.WEB_AUTH_ENDPOINT,
    serverSigningKey: tomlResponse.SIGNING_KEY,
    publicKey: custodialPublicKey || publicKey,
    homeDomain: normalizeHomeDomainUrl(homeDomain).host,
    clientDomain,
    memoId: custodialMemoId,
  });

  // SEP-10 sign
  const signedChallengeTransaction = await sep10AuthSign({
    secretKey: custodialSecretKey || secretKey,
    networkPassphrase: networkConfig.network,
    challengeTransaction,
    walletBackendEndpoint,
  });

  // SEP-10 send
  return await sep10AuthSend({
    authEndpoint: tomlResponse.WEB_AUTH_ENDPOINT,
    signedChallengeTransaction,
  });
};

/**
 * Reusable SEP-45 authentication utility
 * Returns JWT token for authenticated requests
 */
export const authenticateWithSep45 = async (
  anchorActionType: AnchorActionType,
  assetCode: string,
  assetIssuer: string,
  contractId: string,
  clientDomain: string,
  homeDomain: string,
  requiredKeys: TomlFields[],
  sepName: string,
  walletBackendEndpoint: string,
): Promise<string> => {
  log.instruction({
    title: `Initiating a ${sepName} for classic account`,
  });

  const networkConfig = getNetworkConfig();

  // Check Anchor TOML for required SEP-45 fields
  const tomlResponse = await checkTomlForFields({
    sepName,
    assetIssuer: assetIssuer,
    requiredKeys,
    networkUrl: networkConfig.url,
    homeDomain,
  });

  await  checkDepositWithdrawInfo ({
    type: anchorActionType,
    transferServerUrl: sepName.startsWith("SEP-24") ? tomlResponse.TRANSFER_SERVER_SEP0024 : tomlResponse.TRANSFER_SERVER,
    assetCode,
  })

  let clientDomainSigningKey = "";
  if (!isEmpty(clientDomain)) {
    const clientTomlResponse = await getToml(clientDomain);
    clientDomainSigningKey = clientTomlResponse.SIGNING_KEY!;
  }

  log.instruction({
    title: `${sepName} is enabled, and requires authentication so we should go through SEP-45`,
  });

  // SEP-45 start
  const challengeTransaction = await sep45AuthStart({
    authEndpoint: tomlResponse.WEB_AUTH_FOR_CONTRACTS_ENDPOINT,
    contractAddress: contractId,
    homeDomain: normalizeHomeDomainUrl(homeDomain).host,
    clientDomain,
  });

  // SEP-45 sign
  const expectedArgs = {
    account: contractId,
    home_domain: normalizeHomeDomainUrl(homeDomain).host,
    web_auth_domain:  new URL(tomlResponse.WEB_AUTH_FOR_CONTRACTS_ENDPOINT).host,
    web_auth_domain_account: tomlResponse.SIGNING_KEY,
    ...(clientDomain && { client_domain: clientDomain }),
    ...(clientDomainSigningKey && { client_domain_account: clientDomainSigningKey }),
  } as const;

  const signedChallengeResponse = await sep45AuthSign({
    authEntries: challengeTransaction.authorizationEntries,
    clientDomainSigningKey,
    expectedArgs,
    serverSigningKey: tomlResponse.SIGNING_KEY,
    walletBackendEndpoint,
    webAuthContractId: tomlResponse.WEB_AUTH_CONTRACT_ID,
  });

  // SEP-45 send
  return await sep45AuthSend({
    authEndpoint: tomlResponse.WEB_AUTH_FOR_CONTRACTS_ENDPOINT,
    signedChallengeResponse,
  });
};